import {
  useGetPaymentsList,
  useGetTaxPayments,
} from "@/lib/api-client/paths/stripe";
import { MonthPicker } from "@mantine/dates";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BiExport } from "react-icons/bi";
import { useSearchParams } from "react-router";
import type {
  GetPaymentsList,
  GetTaxPayments,
} from "../../../../../server/src/types";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Button as HeroUIButton,
  Checkbox,
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Skeleton,
} from "@heroui/react";
import { Filter, FilterX } from "lucide-react";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { PaymentDetailsModal } from "./PaymentDetailsModal";
import { StatusBadge } from "./StatusBadge";
import { LimePagination } from "@/Components/NextBase/LimePagination";
import { useForm } from "@mantine/form";

type RowPayment = {
  id: string;
  key: string;
  status: string | null;
  payment: string;
  paymentDate: string;
  appointmentDate: string;
  amount: string;
};

const StatusFilter = {
  succeeded: "succeeded",
  failed: "failed",
  processing: "processing",
  refunded: "refunded",
} as const;
type StatusFilter = keyof typeof StatusFilter;
export const PaymentsOverviewTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageParam = searchParams.get("page");
  const perPageParam = searchParams.get("perPage");
  const totalPagesParam = searchParams.get("totalPages");
  const uidParam = searchParams.get("uid");
  const search = searchParams.get("q") ?? "";

  const page = pageParam ? Number(pageParam) : 1;
  const perPage = perPageParam ? Number(perPageParam) : 10;
  const totalPages = totalPagesParam ? Number(totalPagesParam) : 1;
  const selectedPaymentId = uidParam ? Number(uidParam) : undefined;

  const [paymentsExportDateRange, setPaymentsExportDateRange] =
    useState<Date | null>(null);

  /**
   * FILTERS
   */
  const filterPaymentStatuses = searchParams.getAll("status");

  // Memoize the query parameters to prevent unnecessary refetches
  const queryParams = useMemo(
    () => ({
      search,
      page,
      perPage,
      statusFilters:
        filterPaymentStatuses as GetPaymentsList["query"]["statusFilters"],
    }),
    [search, page, perPage, filterPaymentStatuses],
  );

  const { data, isPending, isSuccess } = useGetPaymentsList(queryParams);

  const { mutateAsync: getTaxPayments, isPending: isGettingTaxPayments } =
    useGetTaxPayments();

  const exportPayments = async ({
    format,
  }: {
    format: GetTaxPayments["query"]["format"];
  }) => {
    if (!paymentsExportDateRange) {
      return;
    }

    const startDate = dayjs(paymentsExportDateRange)
      .startOf("month")
      .format("YYYY-MM-DD");
    const endDate = dayjs(paymentsExportDateRange)
      .endOf("month")
      .format("YYYY-MM-DD");

    const response = await getTaxPayments({
      startDate,
      endDate,
      format,
    });

    const paymentsString = response.payments;
    const feesString = response.fees;

    if (!paymentsString || !feesString) {
      return;
    }

    downloadFile({
      content: paymentsString,
      type: response.type,
      startDate,
      endDate,
      exportType: "payments",
      format,
    });

    downloadFile({
      content: feesString,
      type: response.type,
      startDate,
      endDate,
      exportType: "fees",
      format,
    });
  };

  const downloadFile = ({
    content,
    type,
    format,
    startDate,
    endDate,
    exportType,
  }: {
    content: string;
    type: string;
    format: "csv" | "xml";
    startDate: string;
    endDate: string;
    exportType: "payments" | "fees";
  }) => {
    const fileType = `${type};charset=utf-8;`;
    const blob = new Blob([content], { type: fileType });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${exportType}_${startDate}_${endDate}.${format}`,
    );

    document.body.appendChild(link);

    link.click();
    link.parentNode?.removeChild(link);
  };

  /**
   * Update total pages in URL when data changes
   */
  useEffect(() => {
    if (selectedPaymentId != null) return;

    if (data?.pagination.pageCount != null) {
      const newTotalPages = Math.max(1, data.pagination.pageCount);
      if (newTotalPages !== totalPages) {
        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set("totalPages", newTotalPages.toString());
            if (page > newTotalPages) {
              newParams.set("page", "1");
            }
            return newParams;
          },
          { replace: true },
        );
      }
    }
  }, [data?.pagination.pageCount, page, totalPages, setSearchParams]);

  const nonProcessingPayments = data?.paymentsList.filter(
    (payment) => payment.status !== "requires_payment_method",
  );
  const processingPayments = data?.paymentsList.filter(
    (payment) => payment.status === "requires_payment_method",
  );

  const renderCell = useCallback((item: RowPayment, columnKey: React.Key) => {
    const cellValue = item[columnKey as keyof typeof item];

    switch (columnKey) {
      case "status": {
        const castedValue =
          cellValue as GetPaymentsList["response"]["paymentsList"][number]["status"];

        return <StatusBadge payment={{ status: castedValue }} />;
      }
      default:
        return cellValue as string;
    }
  }, []);

  const columns = [
    {
      key: "status",
      label: t`Status`,
    },
    {
      key: "payment",
      label: t`Naročilo`,
    },
    {
      key: "paymentDate",
      label: t`Datum plačila`,
    },
    {
      key: "appointmentDate",
      label: t`Datum termina`,
    },
    {
      key: "amount",
      label: t`Znesek`,
    },
  ];

  const nonProcessingRows: RowPayment[] = isPending
    ? Array(perPage)
        .fill(null)
        .map((_, index) => {
          return {
            id: index.toString(),
            key: `loading-${index}`,
            status: "loading",
            payment: "loading",
            paymentDate: "loading",
            appointmentDate: "loading",
            amount: "00,00",
          };
        })
    : (nonProcessingPayments?.map((payment) => {
        return {
          id: payment.userAppointmentId.toString(),
          key: payment.paymentIntentId!,
          status: payment.status,
          payment: payment.payment,
          paymentDate: payment.date,
          appointmentDate: payment.appointmentDate,
          amount: payment.amountFormatted,
        };
      }) ?? []);

  const processingRows: RowPayment[] = isPending
    ? Array(perPage)
        .fill(null)
        .map((_, index) => {
          return {
            id: index.toString(),
            key: `loading-${index}`,
            status: "loading",
            payment: "loading",
            paymentDate: "loading",
            appointmentDate: "loading",
            amount: "00,00",
          };
        })
    : (processingPayments?.map((payment) => {
        return {
          id: payment.userAppointmentId.toString(),
          key: payment.paymentIntentId!,
          status: payment.status,
          payment: payment.payment,
          paymentDate: payment.date,
          appointmentDate: payment.appointmentDate,
          amount: payment.amountFormatted,
        };
      }) ?? []);

  const handleRowClick = useCallback(
    (key: React.Key) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("uid", key.toString());
          return newParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleModalClose = useCallback(() => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("uid");
        return newParams;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("page", newPage.toString());
          return newParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handlePerPageChange = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("perPage", value);
          newParams.set("page", "1");
          return newParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <>
      <div>
        <div className="flex flex-col gap-1">
          <div className="flex justify-between">
            <Popover
              showArrow
              shadow="md"
              onChange={(opened) => {
                if (opened) {
                  setPaymentsExportDateRange(null);
                }
              }}
            >
              <PopoverTrigger>
                <Button
                  variant="flat"
                  startContent={<BiExport />}
                  className="w-fit"
                >
                  <Trans>Izvozi plačila za računovodstvo</Trans>
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="flex flex-col gap-1">
                  <div className="flex flex-col gap-1">
                    <MonthPicker
                      value={paymentsExportDateRange}
                      onChange={setPaymentsExportDateRange}
                    />
                    <div className="flex gap-1">
                      <Button
                        onPress={() => exportPayments({ format: "csv" })}
                        isLoading={isGettingTaxPayments}
                        fullWidth
                        color="primary"
                      >
                        <Trans>Izvozi CSV</Trans>
                      </Button>
                      <Button
                        onPress={() => exportPayments({ format: "xml" })}
                        isLoading={isGettingTaxPayments}
                        fullWidth
                        color="primary"
                      >
                        <Trans>Izvozi XML</Trans>
                      </Button>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            <StatusFilterPopover />
          </div>

          {filterPaymentStatuses.includes("processing") && (
            <Table
              selectionMode="single"
              onRowAction={handleRowClick}
              isStriped
              aria-label={t`Tabela plačil`}
              className="mt-4"
              isHeaderSticky
            >
              <TableHeader columns={columns}>
                {(column) => (
                  <TableColumn key={column.key}>{column.label}</TableColumn>
                )}
              </TableHeader>
              <TableBody items={processingRows}>
                {(item) => (
                  <TableRow key={item.id}>
                    {(columnKey) => (
                      <TableCell>{renderCell(item, columnKey)}</TableCell>
                    )}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}

          <Table
            selectionMode="single"
            onRowAction={handleRowClick}
            isStriped
            aria-label={t`Tabela plačil`}
            className="mt-2"
          >
            <TableHeader columns={columns}>
              {(column) => (
                <TableColumn key={column.key}>{column.label}</TableColumn>
              )}
            </TableHeader>
            <TableBody
              items={nonProcessingRows}
              emptyContent={isPending ? undefined : t`Ni plačil`}
            >
              {(item) => (
                <TableRow key={item.id}>
                  {(columnKey) => (
                    <TableCell>
                      <Skeleton isLoaded={!isPending} className="rounded-xl">
                        {renderCell(item, columnKey)}
                      </Skeleton>
                    </TableCell>
                  )}
                </TableRow>
              )}
            </TableBody>
          </Table>

          <div className="mt-4">
            <LimePagination
              paginationProps={{
                isCompact: true,
                color: "primary",
                page,
                total: totalPages,
                onChange: handlePageChange,
              }}
              perPageProps={{
                label: t`plačil`,
                values: [10, 20, 50, 100],
                value: perPage.toString(),
                onChange: handlePerPageChange,
              }}
            />
          </div>

          {isSuccess && data.includesPaymentsBefore17Oct && (
            <LimeAlert
              title={t`Obvestilo`}
              message={
                <Trans>
                  V seznamu so prikazana plačila od 17. 10. 2023 dalje. Za
                  podatke o plačilih pred tem datumom, prosimo, da nas
                  kontaktirate.
                </Trans>
              }
            ></LimeAlert>
          )}
        </div>
      </div>

      <PaymentDetailsModal
        userAppointmentId={selectedPaymentId}
        onClose={handleModalClose}
      />
    </>
  );
};

const StatusFilterPopover = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filterPaymentStatuses = searchParams.getAll("status");

  const form = useForm<{
    status: StatusFilter[];
  }>({
    initialValues: {
      status: filterPaymentStatuses as StatusFilter[],
    },
  });

  const toggleFilterPaymentStatus = (status: StatusFilter) => {
    if (form.values.status.includes(status)) {
      form.setFieldValue(
        "status",
        form.values.status.filter((s) => s !== status),
      );
    } else {
      form.setFieldValue("status", [...form.values.status, status]);
    }
  };

  const setFilterPaymentStatuses = (statuses: StatusFilter[]) => {
    form.setFieldValue("status", statuses);
  };

  const handleSubmit = (values: typeof form.values) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("status");
      values.status.forEach((status) => {
        newParams.append("status", status);
      });
      return newParams;
    });
  };

  return (
    <Popover placement="bottom-end" showArrow>
      <PopoverTrigger>
        <HeroUIButton isIconOnly variant="flat">
          {filterPaymentStatuses.length !== Object.keys(StatusFilter).length ? (
            <FilterX />
          ) : (
            <Filter />
          )}
        </HeroUIButton>
      </PopoverTrigger>
      <PopoverContent>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <div className="min-w-72 px-1 py-2">
            <p className="mb-2 text-lg font-medium capitalize">
              <Trans>Filter</Trans>
            </p>
            <div>
              <div className="mb-4 flex items-center justify-between">
                <p className="text-base">
                  <Trans>Tipi</Trans>
                </p>

                <HeroUIButton
                  color={
                    form.getValues().status.length ===
                    Object.keys(StatusFilter).length
                      ? "primary"
                      : "default"
                  }
                  onPress={() => {
                    if (
                      form.getValues().status.length ===
                      Object.keys(StatusFilter).length
                    ) {
                      setFilterPaymentStatuses([]);
                    } else {
                      setFilterPaymentStatuses(Object.values(StatusFilter));
                    }
                  }}
                >
                  <Trans>Izberi vse</Trans>
                </HeroUIButton>
              </div>

              <div className="flex flex-col gap-2 rounded-md bg-[#fafafa] p-2">
                {[
                  {
                    label: t`Uspešno`,
                    value: "succeeded",
                    checked: form.getValues().status.includes("succeeded"),
                    onChange: () => toggleFilterPaymentStatus("succeeded"),
                  },
                  {
                    label: t`Neuspešno`,
                    value: "failed",
                    checked: form.getValues().status.includes("failed"),
                    onChange: () => toggleFilterPaymentStatus("failed"),
                  },
                  {
                    label: t`V obdelavi`,
                    value: "processing",
                    checked: form.getValues().status.includes("processing"),
                    onChange: () => toggleFilterPaymentStatus("processing"),
                  },
                  {
                    label: t`Povrnjeno`,
                    value: "refunded",
                    checked: form.getValues().status.includes("refunded"),
                    onChange: () => toggleFilterPaymentStatus("refunded"),
                  },
                ]?.map((row) => {
                  return (
                    <Checkbox
                      key={row.value}
                      isSelected={row.checked}
                      onValueChange={row.onChange}
                      value={row.value}
                    >
                      {row.label}
                    </Checkbox>
                  );
                })}
              </div>

              <div className="flex justify-end">
                <Button color="primary" type="submit" className="mt-2">
                  <Trans>Shrani</Trans>
                </Button>
              </div>
            </div>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
};
